import { Paths, routeLabels } from "@app/routes";
import {
  usePermission,
  useSessionData,
  useTranslation,
  useZodSearchParams,
} from "@atlas/sdk";
import type { IHubCatalogType } from "@models";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useHubCatalogByVersion,
  useHubCatalogType,
  useHubCatalogTypes,
  useHubCurrentCart,
  useHubPackage,
  useHubPackageDeployments,
  useUserInfo,
} from "@query";
import {
  APP_BASE_PATH,
  defaultHubCartPackageId,
  HUB_PERM,
  useBreadcrumbContext,
} from "@shared";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { z } from "zod";

const breadcrumbSearchParamsSchema = z.object({
  view: z.string().optional(),
  tab: z.string().optional(),
  catalogId: z.coerce.number().optional(),
  solution: z.coerce.number().optional(),
  solutionCatalogId: z.coerce.number().optional(),
  packageId: z.coerce.number().optional(),
  deploymentId: z.coerce.number().optional(),
  typeId: z.coerce.number().optional(),
  version: z.string().optional(),
  fromTab: z.string().optional(),
});

const CATALOG_DETAILS_BREADCRUMB_TABS = [
  "explorer",
  "myContributions",
  "subscriptions",
] as const;

type CatalogDetailsBreadcrumbTab =
  (typeof CATALOG_DETAILS_BREADCRUMB_TABS)[number];

function catalogDetailsBreadcrumbTabFromSearch(
  tab: string | null,
): CatalogDetailsBreadcrumbTab | null {
  if (
    tab === "explorer" ||
    tab === "myContributions" ||
    tab === "subscriptions"
  ) {
    return tab;
  }
  return null;
}

function catalogDetailsTabSegmentLabel(
  tab: CatalogDetailsBreadcrumbTab,
  translation: (key: string, options?: { defaultValue?: string }) => string,
): string {
  switch (tab) {
    case "explorer":
      return translation("hub.catalog_manager.explorer", {
        defaultValue: "Explorer",
      });
    case "myContributions":
      return translation("hub.catalog_manager.my_contributions", {
        defaultValue: "My Catalogs",
      });
    case "subscriptions":
      return translation("hub.catalog_manager.subscriptions", {
        defaultValue: "My Subscriptions",
      });
    default:
      return tab;
  }
}

export function useBreadcrumbs() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t: translation } = useTranslation();
  const { userId } = useSessionData();
  const uid = (userId ?? "").trim();
  const { contributeCatalogLabel } = useBreadcrumbContext();

  const pathNamesEarly = useMemo(
    () => location.pathname.split("/").filter((x) => x),
    [location.pathname],
  );
  const isPackageManagerPath = useMemo(
    () =>
      pathNamesEarly.length === 1 && pathNamesEarly[0] === Paths.PackageManager,
    [pathNamesEarly],
  );

  const { data: userHubCart } = useHubCurrentCart(uid, {
    enabled: uid.length > 0 && isPackageManagerPath,
  });
  const defaultHubCartId = useMemo(
    () => defaultHubCartPackageId(userHubCart),
    [userHubCart],
  );

  const {
    view,
    tab,
    catalogId = 0,
    solution = 0,
    solutionCatalogId = 0,
    packageId = 0,
    deploymentId = 0,
    typeId = 0,
    version: versionFromUrl,
    fromTab: rawFromTab,
  } = useZodSearchParams(breadcrumbSearchParamsSchema);

  const hubCartPkgId = packageId > 0 ? packageId : defaultHubCartId;

  const { data: hubCartPackage } = useHubPackage(hubCartPkgId, {
    enabled: isPackageManagerPath && hubCartPkgId > 0,
  });
  const { data: userInfo } = useUserInfo(APP_BASE_PATH);
  const companyId = userInfo?.companyId ?? 0;
  const { isPermission: hasCatalogBuy } = usePermission([HUB_PERM.CATALOG_BUY]);
  // Only needed for package-manager deployment breadcrumb labels — not on home.
  const needsDeploymentsForBreadcrumb =
    hasCatalogBuy && isPackageManagerPath && rawFromTab === "deployments";
  const { data: deployments = [] } = useHubPackageDeployments(companyId, {
    enabled: needsDeploymentsForBreadcrumb,
  });

  const hubCartPackageDisplayName = useMemo(() => {
    if (rawFromTab === "deployments") {
      const deployment = deployments.find((d) =>
        deploymentId > 0
          ? d.deploymentId === deploymentId
          : d.packageId === hubCartPkgId,
      );
      if (deployment) {
        if (deployment.name) return deployment.name;

        const solutionConfig = deployment.config?.find((c) => c.solution);
        const solutionLabel = solutionConfig?.solution?.label;
        if (solutionLabel) return solutionLabel;
      }
    }

    const raw = (hubCartPackage?.label ?? hubCartPackage?.name ?? "").trim();
    if (raw.length > 0) return raw;
    return translation("hub.header.cart_package_fallback", {
      defaultValue: "Package",
    });
  }, [
    hubCartPackage,
    translation,
    rawFromTab,
    deployments,
    hubCartPkgId,
    deploymentId,
  ]);

  const editCatalogTypeId = useMemo(() => {
    if (!pathNamesEarly.includes(Paths.EditCatalogType)) return 0;
    return typeId > 0 ? typeId : 0;
  }, [pathNamesEarly, typeId]);

  const editCatalogId = useMemo(() => {
    if (pathNamesEarly.length < 2) return 0;
    if (pathNamesEarly[pathNamesEarly.length - 1] !== Paths.EditCatalog)
      return 0;
    if (pathNamesEarly[pathNamesEarly.length - 2] !== Paths.CatalogManager)
      return 0;
    return catalogId > 0 ? catalogId : 0;
  }, [pathNamesEarly, catalogId]);

  const reviewCatalogId = useMemo(() => {
    if (!pathNamesEarly.includes(Paths.ReviewCatalog)) return 0;
    return catalogId > 0 ? catalogId : 0;
  }, [pathNamesEarly, catalogId]);

  const catalogDetailsId = useMemo(() => {
    if (!pathNamesEarly.includes(Paths.CatalogDetails)) return 0;
    return catalogId > 0 ? catalogId : 0;
  }, [pathNamesEarly, catalogId]);

  const subscribeSolutionCatalogId = useMemo(() => {
    if (pathNamesEarly.length < 2) return 0;
    if (pathNamesEarly[pathNamesEarly.length - 1] !== Paths.SubscribeSolution)
      return 0;
    if (pathNamesEarly[pathNamesEarly.length - 2] !== Paths.CatalogManager)
      return 0;
    if (solution > 0) return solution;
    if (solutionCatalogId > 0) return solutionCatalogId;
    return catalogId > 0 ? catalogId : 0;
  }, [pathNamesEarly, solution, solutionCatalogId, catalogId]);

  const packageManagerSolutionCatalogId = useMemo(() => {
    if (
      pathNamesEarly.length !== 1 ||
      pathNamesEarly[0] !== Paths.PackageManager
    )
      return 0;
    if (packageId > 0 && rawFromTab !== "packages") return packageId;
    if (solution > 0) return solution;
    if (solutionCatalogId > 0) return solutionCatalogId;
    return catalogId > 0 ? catalogId : 0;
  }, [
    pathNamesEarly,
    packageId,
    rawFromTab,
    solution,
    solutionCatalogId,
    catalogId,
  ]);

  const installFlowSolutionCatalogId = useMemo(
    () =>
      packageManagerSolutionCatalogId > 0
        ? packageManagerSolutionCatalogId
        : subscribeSolutionCatalogId,
    [packageManagerSolutionCatalogId, subscribeSolutionCatalogId],
  );

  const { data: catalogTypes = [] } = useHubCatalogTypes(
    {
      mode: "DATA",
      sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
      startIndex: 0,
      limit: 100,
    },
    { enabled: editCatalogTypeId > 0 },
  );
  const { data: editCatalogType, isSuccess: editDetailSuccess } =
    useHubCatalogType(editCatalogTypeId, {
      enabled: editCatalogTypeId > 0,
    });

  const { data: editCatalog, isSuccess: editCatalogDetailSuccess } =
    useHubCatalogByVersion(editCatalogId, versionFromUrl ?? "", {
      enabled: editCatalogId > 0 && Boolean(versionFromUrl),
    });

  const { data: reviewCatalog, isSuccess: reviewCatalogDetailSuccess } =
    useHubCatalogByVersion(reviewCatalogId, versionFromUrl ?? "", {
      enabled: reviewCatalogId > 0 && Boolean(versionFromUrl),
    });

  const { data: detailsCatalog, isSuccess: detailsCatalogDetailSuccess } =
    useHubCatalogByVersion(catalogDetailsId, versionFromUrl ?? "", {
      enabled: catalogDetailsId > 0 && Boolean(versionFromUrl),
    });

  const {
    data: installFlowSolutionCatalog,
    isSuccess: installFlowSolutionCatalogSuccess,
  } = useHubCatalogByVersion(
    installFlowSolutionCatalogId,
    versionFromUrl ?? "",
    {
      enabled: installFlowSolutionCatalogId > 0 && Boolean(versionFromUrl),
    },
  );

  const editCatalogTypeDisplayName = useMemo(() => {
    if (editCatalogTypeId <= 0) return "";

    const fromList = catalogTypes.find(
      (ct: IHubCatalogType) => ct.typeId === editCatalogTypeId,
    );
    const listName = fromList?.label?.trim() || fromList?.name?.trim() || "";
    if (listName.length > 0) return listName;

    if (
      editDetailSuccess &&
      editCatalogType &&
      editCatalogType.typeId === editCatalogTypeId
    ) {
      return (
        editCatalogType.label?.trim() || editCatalogType.name?.trim() || ""
      );
    }

    return "";
  }, [editCatalogType, editCatalogTypeId, editDetailSuccess, catalogTypes]);

  const editCatalogVersion = useMemo(() => {
    if (editCatalogId <= 0) return "";
    if (
      editCatalogDetailSuccess &&
      editCatalog &&
      editCatalog.catalogId === editCatalogId
    ) {
      return String(editCatalog.version ?? "").trim() || "";
    }
    return "";
  }, [editCatalog, editCatalogDetailSuccess, editCatalogId]);

  const editCatalogDisplayName = useMemo(() => {
    if (editCatalogId <= 0) return "";
    if (
      editCatalogDetailSuccess &&
      editCatalog &&
      editCatalog.catalogId === editCatalogId
    ) {
      return editCatalog.label?.trim() || editCatalog.name?.trim() || "";
    }
    return "";
  }, [editCatalog, editCatalogDetailSuccess, editCatalogId]);

  const reviewCatalogDisplayName = useMemo(() => {
    if (reviewCatalogId <= 0) return "";
    if (
      reviewCatalogDetailSuccess &&
      reviewCatalog &&
      reviewCatalog.catalogId === reviewCatalogId
    ) {
      return reviewCatalog.label?.trim() || reviewCatalog.name?.trim() || "";
    }
    return "";
  }, [reviewCatalog, reviewCatalogDetailSuccess, reviewCatalogId]);

  const detailsCatalogDisplayName = useMemo(() => {
    if (catalogDetailsId <= 0) return "";
    if (
      detailsCatalogDetailSuccess &&
      detailsCatalog &&
      detailsCatalog.catalogId === catalogDetailsId
    ) {
      return detailsCatalog.label?.trim() || detailsCatalog.name?.trim() || "";
    }
    return "";
  }, [detailsCatalog, detailsCatalogDetailSuccess, catalogDetailsId]);

  const installFlowSolutionCatalogDisplayName = useMemo(() => {
    if (installFlowSolutionCatalogId <= 0) return "";
    if (
      installFlowSolutionCatalogSuccess &&
      installFlowSolutionCatalog &&
      installFlowSolutionCatalog.catalogId === installFlowSolutionCatalogId
    ) {
      return (
        installFlowSolutionCatalog.label?.trim() ||
        installFlowSolutionCatalog.name?.trim() ||
        ""
      );
    }
    return "";
  }, [
    installFlowSolutionCatalog,
    installFlowSolutionCatalogSuccess,
    installFlowSolutionCatalogId,
  ]);

  const pathNames = pathNamesEarly;

  const packageManagerTab = useMemo(() => {
    if (tab === "deployments") return "deployments";
    if (tab === Paths.PackageManagerViewPackages) return "packages";
    if (view === Paths.PackageManagerViewPackages) return "packages";
    return null;
  }, [tab, view]);

  const hubManagerUrlTab = tab ?? null;

  const catalogDetailsSourceTab = useMemo(
    () => catalogDetailsBreadcrumbTabFromSearch(hubManagerUrlTab),
    [hubManagerUrlTab],
  );

  const reviewHubManagerSourceTab = useMemo(
    (): "reviews" | null => (hubManagerUrlTab === "reviews" ? "reviews" : null),
    [hubManagerUrlTab],
  );

  const breadcrumbs: Array<{
    label: string;
    badge?: string;
    onClick: () => void;
  }> = [];

  // Show Home breadcrumb when on home screen (path is / or /home)
  const isHomePage =
    pathNames.length === 0 ||
    (pathNames.length === 1 && pathNames[0] === Paths.Home);
  if (isHomePage) {
    breadcrumbs.push({
      label: translation(routeLabels[Paths.Home] ?? "hub.sidebar.home"),
      onClick: () => navigate(`/${Paths.Home}`),
    });
    return breadcrumbs;
  }

  if (isPackageManagerPath && packageManagerTab != null) {
    breadcrumbs.push({
      label: translation(
        routeLabels[Paths.PackageManager] ?? "hub.sidebar.package_manager",
        { defaultValue: "Package Manager" },
      ),
      onClick: () => {
        navigate(
          `/${Paths.PackageManager}?tab=${encodeURIComponent(Paths.PackageManagerViewPackages)}`,
        );
      },
    });
    const tabLabel =
      packageManagerTab === "deployments"
        ? translation("hub.package_manager.tab_deployments")
        : translation("hub.package_manager.tab_packages");
    breadcrumbs.push({
      label: tabLabel,
      onClick: () => {
        navigate(
          `/${Paths.PackageManager}?tab=${encodeURIComponent(packageManagerTab)}`,
        );
      },
    });
    return breadcrumbs;
  }

  if (isPackageManagerPath && packageManagerSolutionCatalogId > 0) {
    breadcrumbs.push({
      label: translation(
        routeLabels[Paths.PackageManager] ?? "hub.sidebar.package_manager",
        { defaultValue: "Package Manager" },
      ),
      onClick: () => {
        navigate(`/${Paths.PackageManager}`);
      },
    });

    const fromTab = rawFromTab || "packages";
    if (fromTab === "deployments" || fromTab === "packages") {
      const tabLabel =
        fromTab === "deployments"
          ? translation("hub.package_manager.tab_deployments", {
              defaultValue: "Deployments",
            })
          : translation("hub.package_manager.tab_packages", {
              defaultValue: "Packages",
            });
      breadcrumbs.push({
        label: tabLabel,
        onClick: () => {
          navigate(
            `/${Paths.PackageManager}?tab=${encodeURIComponent(fromTab)}`,
          );
        },
      });
    }

    breadcrumbs.push({
      label: hubCartPackageDisplayName,
      onClick: () => {
        const cartParam = deploymentId > 0 ? "" : `packageId=${hubCartPkgId}`;
        const deployParam =
          deploymentId > 0 ? `deploymentId=${deploymentId}` : "";
        const tabParam = fromTab ? `fromTab=${fromTab}` : "";
        const queryParams = [cartParam, deployParam, tabParam]
          .filter(Boolean)
          .join("&");
        navigate(`/${Paths.PackageManager}?${queryParams}`);
      },
    });
    breadcrumbs.push({
      label: translation("hub.package_manager.breadcrumb_install", {
        defaultValue: "Install",
      }),
      onClick: () => {
        navigate({
          pathname: `/${Paths.PackageManager}`,
          search: location.search,
        });
      },
    });
    return breadcrumbs;
  }

  if (isPackageManagerPath) {
    breadcrumbs.push({
      label: translation(
        routeLabels[Paths.PackageManager] ?? "hub.sidebar.package_manager",
        { defaultValue: "Package Manager" },
      ),
      onClick: () => {
        navigate(
          `/${Paths.PackageManager}?tab=${encodeURIComponent(Paths.PackageManagerViewPackages)}`,
        );
      },
    });

    const fromTab = rawFromTab || "packages";
    if (fromTab === "deployments" || fromTab === "packages") {
      const tabLabel =
        fromTab === "deployments"
          ? translation("hub.package_manager.tab_deployments", {
              defaultValue: "Deployments",
            })
          : translation("hub.package_manager.tab_packages", {
              defaultValue: "Packages",
            });
      breadcrumbs.push({
        label: tabLabel,
        onClick: () => {
          navigate(
            `/${Paths.PackageManager}?tab=${encodeURIComponent(fromTab)}`,
          );
        },
      });
    }

    breadcrumbs.push({
      label: hubCartPackageDisplayName,
      onClick: () => {
        const cartParam = deploymentId > 0 ? "" : `packageId=${hubCartPkgId}`;
        const deployParam =
          deploymentId > 0 ? `deploymentId=${deploymentId}` : "";
        const tabParam = fromTab ? `fromTab=${fromTab}` : "";
        const queryParams = [cartParam, deployParam, tabParam]
          .filter(Boolean)
          .join("&");
        navigate(`/${Paths.PackageManager}?${queryParams}`);
      },
    });
    return breadcrumbs;
  }

  let currentPath = "";

  pathNames.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const absolutePath = currentPath;

    // Skip Home segment
    if (segment === Paths.Home) {
      return;
    }

    if (
      segment === Paths.EditCatalog &&
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogManager
    ) {
      const label =
        editCatalogDisplayName.length > 0
          ? translation("hub.catalog_manager.breadcrumb_edit_name", {
              catalogName: editCatalogDisplayName,
              defaultValue: `Edit: ${editCatalogDisplayName}`,
            })
          : translation(
              routeLabels[Paths.EditCatalog] ?? "hub.routes.edit_catalog",
            );
      const search =
        editCatalogId > 0 ? `?catalogId=${editCatalogId}` : location.search;
      breadcrumbs.push({
        label,
        badge: editCatalogVersion ? `v${editCatalogVersion}` : undefined,
        onClick: () => {
          navigate({ pathname: absolutePath, search });
        },
      });
    } else if (
      segment === Paths.ReviewCatalog &&
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogManager &&
      reviewCatalogId > 0
    ) {
      if (reviewHubManagerSourceTab != null) {
        breadcrumbs.push({
          label: translation("hub.catalog_manager.reviews", {
            defaultValue: "Review",
          }),
          onClick: () => {
            navigate(
              `/${Paths.CatalogManager}?tab=${encodeURIComponent(reviewHubManagerSourceTab)}`,
            );
          },
        });
      }
      const label =
        reviewCatalogDisplayName.length > 0
          ? reviewCatalogDisplayName
          : translation(
              routeLabels[Paths.ReviewCatalog] ?? "hub.routes.review_catalog",
            );
      breadcrumbs.push({
        label,
        onClick: () => {
          navigate({
            pathname: `/${Paths.CatalogManager}/${Paths.ReviewCatalog}`,
            search: location.search,
          });
        },
      });
    } else if (
      segment === Paths.CatalogDetails &&
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogManager &&
      catalogDetailsId > 0
    ) {
      if (catalogDetailsSourceTab != null) {
        breadcrumbs.push({
          label: catalogDetailsTabSegmentLabel(
            catalogDetailsSourceTab,
            translation,
          ),
          onClick: () => {
            navigate(
              `/${Paths.CatalogManager}?tab=${encodeURIComponent(catalogDetailsSourceTab)}`,
            );
          },
        });
      }
      const label =
        detailsCatalogDisplayName.length > 0
          ? detailsCatalogDisplayName
          : translation(
              routeLabels[Paths.CatalogDetails] ?? "hub.routes.catalog_details",
            );
      breadcrumbs.push({
        label,
        onClick: () => {
          navigate({
            pathname: `/${Paths.CatalogManager}/${Paths.CatalogDetails}`,
            search: location.search,
          });
        },
      });
    } else if (
      segment === Paths.SubscribeSolution &&
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogManager &&
      subscribeSolutionCatalogId > 0
    ) {
      if (catalogDetailsSourceTab != null) {
        breadcrumbs.push({
          label: catalogDetailsTabSegmentLabel(
            catalogDetailsSourceTab,
            translation,
          ),
          onClick: () => {
            navigate(
              `/${Paths.CatalogManager}?tab=${encodeURIComponent(catalogDetailsSourceTab)}`,
            );
          },
        });
      }
      const label =
        installFlowSolutionCatalogDisplayName.length > 0
          ? installFlowSolutionCatalogDisplayName
          : translation(
              routeLabels[Paths.SubscribeSolution] ??
                "hub.routes.subscribe_solution",
              { defaultValue: "Subscribe" },
            );
      breadcrumbs.push({
        label,
        onClick: () => {
          navigate({
            pathname: `/${Paths.CatalogManager}/${Paths.SubscribeSolution}`,
            search: location.search,
          });
        },
      });
    } else if (
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogManager &&
      segment === Paths.CreateCatalog
    ) {
      const trimmed = contributeCatalogLabel.trim();
      const label =
        trimmed.length > 0
          ? translation("hub.catalog_manager.breadcrumb_contribute_with_name", {
              name: trimmed,
              defaultValue: `Create: ${trimmed}`,
            })
          : translation("hub.catalog_manager.breadcrumb_contribute", {
              defaultValue: "Create",
            });
      breadcrumbs.push({
        label,
        onClick: () => {
          navigate(absolutePath);
        },
      });
    } else if (
      segment === Paths.EditCatalogType &&
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogTypeManager &&
      editCatalogTypeId > 0
    ) {
      const label =
        editCatalogTypeDisplayName.length > 0
          ? translation("hub.catalog_type_manager.breadcrumb_edit_name", {
              catalogTypeName: editCatalogTypeDisplayName,
              defaultValue: `Edit: ${editCatalogTypeDisplayName}`,
            })
          : translation(
              routeLabels[Paths.EditCatalogType] ??
                "hub.routes.edit_catalog_type",
            );
      breadcrumbs.push({
        label,
        onClick: () => {
          navigate({
            pathname: `/${Paths.CatalogTypeManager}/${Paths.EditCatalogType}`,
            search: location.search,
          });
        },
      });
    } else if (
      index > 0 &&
      pathNames[index - 1] === Paths.CatalogTypeManager &&
      segment === Paths.CreateCatalogType
    ) {
      breadcrumbs.push({
        label: translation(
          routeLabels[Paths.CreateCatalogType] ??
            "hub.routes.create_catalog_type",
        ),
        onClick: () => {
          navigate(absolutePath);
        },
      });
    } else if (segment === Paths.ExportList) {
      breadcrumbs.push({
        label: translation("hub.sidebar.token_manager", {
          defaultValue: "Token Manager",
        }),
        onClick: () => {
          navigate(absolutePath);
        },
      });
    } else {
      const key = routeLabels[segment];
      const defaultLabel = routeLabels[segment] ?? segment;
      const label = key
        ? translation(key, { defaultValue: defaultLabel })
        : defaultLabel;

      breadcrumbs.push({
        label,
        onClick: () => {
          navigate(absolutePath);
        },
      });
    }
  });

  return breadcrumbs;
}
